// src/hooks/useMap.js
import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import { handleRightClick } from "../utils/fireManagement";
import { decode as base64_decode } from "base-64";
import { getDistance  } from "geolib";
import { displayDistanceAndTimeInfo, addFlightPathToMap } from "../utils/utils";

mapboxgl.accessToken = "pk.eyJ1IjoiZmhzaW50IiwiYSI6ImNsYWNleWl3dDAycTYzbnBjdzdsc2drNG0ifQ.SRAl1VhLNL3_DztwVBPZYQ";

const useMap = (lng, lat, zoom, selectedFireRef, setSelectedFire, helicopterCurrLoc, initialHelicopterLocationsRef, markers, currentFlyLoc, setHelicopterData, setCurrentFlightIndex) => {
  const mapContainer = useRef(null);
  const map = useRef(null);

  useEffect(() => {
    if (map.current) return; // Initialize map only once

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/fhsint/clacf8lg5000215qu1hphnlin",
      center: [lng, lat],
      zoom: zoom,
      minZoom: 2,
    });

    const userRole = base64_decode(localStorage.getItem("role"));
    if (userRole === 'admin' || userRole === 'user') {
      map.current.on('contextmenu', (e) => handleRightClick(map.current, e, selectedFireRef, setSelectedFire));
    }

    map.current.on('click', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, { 
        layers: ['unclustered-point', 'clusters'] 
      });

      if (features.length === 0 && selectedFireRef.current) {
        selectedFireRef.current.getElement().classList.remove('selected-fire');
        setSelectedFire(null);
      } else if (features.length > 0 && features[0].layer.id === 'clusters') {
        return;
      }
    });

    // Wait for the style to load before adding sources and layers
    map.current.on('style.load', () => {
      console.log("Style loaded and addHelicopterClusterSourceAndLayers");
      addHelicopterClusterSourceAndLayers();
      setupClusterClickHandlers();
    });

    return () => {
      map.current.remove();
    };
  }, [lng, lat, zoom, selectedFireRef, setSelectedFire]);

  useEffect(() => {
    if (map.current) {
        console.log("Updating map with new helicopter locations");

        // Ensure the style is loaded before making changes
        if (!map.current.isStyleLoaded()) {
            map.current.once('style.load', () => {
                updateMapSourcesAndLayers();
            });
        } else {
            updateMapSourcesAndLayers();
        }
    }
}, [helicopterCurrLoc]);

const updateMapSourcesAndLayers = () => {
  // Remove existing layers
    if(helicopterCurrLoc.length > 0){
    if (map.current.getLayer('clusters')) {
        map.current.removeLayer('clusters');
    }
    if (map.current.getLayer('cluster-count')) {
        map.current.removeLayer('cluster-count');
    }
    if (map.current.getLayer('unclustered-point')) {
        map.current.removeLayer('unclustered-point');
    }

    // Remove existing source
    if (map.current.getSource('halicopters')) {
        map.current.removeSource('halicopters');
    }
    addHelicopterClusterSourceAndLayers();
    setupClusterClickHandlers();
  } 

  // Re-add source and layers
  

};

const resetHelicopterLocations = () => {
  console.log("Resetting helicopter locations to initial state", initialHelicopterLocationsRef.current);
  while (helicopterCurrLoc.length > 0) {
      helicopterCurrLoc.pop();
  }
  helicopterCurrLoc.push(...initialHelicopterLocationsRef.current);
  updateMapSourcesAndLayers(initialHelicopterLocationsRef.current);

  
    // Make all line layers invisible
    const allLayers = map.current.getStyle().layers;
    allLayers.forEach(layer => {
        if (layer.id.startsWith('lines-')) {
            map.current.setLayoutProperty(layer.id, 'visibility', 'none');
        }
    });
};

const toggleSidebar = (e, passedHelicopterData = null) => {
  console.log("Toggle sidebar called, these are the initial helicopter locations", initialHelicopterLocationsRef.current);
  let data;
  if (passedHelicopterData) {
    data = passedHelicopterData;
  } else if (e && e.target) {
    const targetElement = e.target.closest('.helicopter-marker');
    if (!targetElement || !targetElement.hasAttribute('data-value')) {
      console.error('Invalid target element for toggleSidebar');
      return;
    }
    data = JSON.parse(targetElement.getAttribute('data-value'));
  } else {
    console.error('No valid data source for toggleSidebar');
    return;
  }

  // Parse the flights property if it's a string
  if (typeof data.flights === 'string') {
    data.flights = JSON.parse(data.flights);
  }

  const lastFlightIndex = data.flights.length - 1;  // Start with the last flight by default

  const trackId = data.trackId || data.id;
  if (!trackId) {
    console.error('No valid trackId found for helicopter');
    return;
  }

  const elem = document.getElementById("left");
  const isSidebarCollapsed = elem.classList.contains('collapsed');

  if (selectedFireRef.current) {
    console.log("Fire is selected");
    const fireCoords = selectedFireRef.current.getLngLat();
    const heliCoords = Array.isArray(data.coordinates) ? data.coordinates : JSON.parse(data.coordinates);

    const distance = getDistance(
      { latitude: fireCoords.lat, longitude: fireCoords.lng },
      { latitude: heliCoords[1], longitude: heliCoords[0] }
    );

    if (isNaN(distance)) {
      console.error("Failed to calculate distance. Check coordinate formats.");
      swal({
        title: "Error",
        text: "Unable to calculate distance. Please try again.",
        icon: "error",
      });
    } else {
      // Add flight path to map
      addFlightPathToMap(map.current, heliCoords, fireCoords);
      // Calculate and display distance and time information
      displayDistanceAndTimeInfo(distance, data, map.current);
    }
  } else {
    if (!isSidebarCollapsed) {
      resetHelicopterLocations();
    }
    console.log("No fire selected");
    // Set the helicopter data and current flight index
    setHelicopterData(data);
    setCurrentFlightIndex(lastFlightIndex);
    console.log("This is the last flight index", lastFlightIndex);

    const currentTrackId = document.getElementById('trackId').getAttribute('data-trackid');

    if (currentTrackId !== trackId) {
      if (currentTrackId) {
        map.current.setLayoutProperty('lines-' + currentTrackId, 'visibility', 'none');
      }

      map.current.setLayoutProperty('lines-' + trackId, 'visibility', 'visible');
      elem.classList.remove('collapsed');
    } else {
      const collapsed = elem.classList.toggle('collapsed');

      if (map.current.getLayer('lines-' + trackId)) {
        const visibility = map.current.getLayoutProperty('lines-' + trackId, 'visibility');

        if (visibility === 'visible') {
          map.current.setLayoutProperty('lines-' + trackId, 'visibility', 'none');
        } else {
          map.current.setLayoutProperty('lines-' + trackId, 'visibility', 'visible');
        }
      } else {
        console.warn(`Layer 'lines-${trackId}' does not exist in the map`);
      }
    }
  }
};

  const setupClusterClickHandlers = () => {
    let clickTimer = null;
    const singleClickTimeout = 250;

    map.current.on("click", "clusters", function (e) {
      if (clickTimer === null) {
        clickTimer = setTimeout(() => {
          clickTimer = null;
          handleSingleClick(e);
        }, singleClickTimeout);
      } else {
        clearTimeout(clickTimer);
        clickTimer = null;
        handleDoubleClick(e);
      }
    });

    map.current.on("mouseenter", "clusters", function () {
      map.current.getCanvas().style.cursor = "pointer";
    });

    map.current.on("mouseleave", "clusters", function () {
      map.current.getCanvas().style.cursor = "";
    });

    map.current.on('render', () => {
      if (!map.current.getSource('halicopters')) return;
      updateMarkers(helicopterCurrLoc);
    });
  };

  const handleSingleClick = (e) => {
    const features = map.current.queryRenderedFeatures(e.point, { layers: ["clusters"] });
    const clusterId = features[0].properties.cluster_id;
    map.current.getSource("halicopters").getClusterLeaves(clusterId, 100, 0, function (err, leaves) {
      if (err) {
        console.error('Error getting cluster leaves:', err);
        return;
      }

      const helicopterLinks = leaves.map(leaf =>
        `<a href="#" class="helicopter-link" data-id="${leaf.properties.id}" data-track-id="${leaf.properties.trackId}">${leaf.properties.title}</a>`
      ).join('');

      const popup = new mapboxgl.Popup()
        .setLngLat(features[0].geometry.coordinates)
        .setHTML(`<div class="popup-content">${helicopterLinks}</div>`)
        .addTo(map.current);

      popup.getElement().querySelectorAll('.helicopter-link').forEach(link => {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          const helicopterId = this.getAttribute('data-id');
          const trackId = this.getAttribute('data-track-id');
          console.log('Clicked helicopter ID:', helicopterId, 'Track ID:', trackId);

          const helicopterData = leaves.find(leaf => leaf.properties.id === helicopterId);

          if (helicopterData) {
            helicopterData.properties.trackId = trackId;
            toggleSidebar(null, helicopterData.properties);
          } else {
            console.error('Could not find data for helicopter:', helicopterId);
          }
        });
      });
    });
  };

  const handleDoubleClick = (e) => {
    const features = map.current.queryRenderedFeatures(e.point, { layers: ["clusters"] });
    const clusterId = features[0].properties.cluster_id;
    map.current
      .getSource("halicopters")
      .getClusterExpansionZoom(clusterId, function (err, zoom) {
        if (err) return;

        const increasedZoom = zoom + 2;

        map.current.easeTo({
          center: features[0].geometry.coordinates,
          zoom: increasedZoom
        });
      });
  };

  const addHelicopterClusterSourceAndLayers = () => {
    console.log("These are the helicopter locations", helicopterCurrLoc);
    map.current.addSource("halicopters", {
      type: "geojson",
      data: { features: helicopterCurrLoc },
      cluster: true,
      clusterMaxZoom: 17,
      clusterRadius: 60,
      clusterProperties: {
        title: ["concat", ['get', 'clusterTitle']]
      }
    });

    map.current.addLayer({
      id: "clusters",
      type: "circle",
      source: "halicopters",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#51bbd6",
        "circle-radius": 20
      }
    });

    map.current.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "halicopters",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12
      }
    });

    map.current.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "halicopters",
      filter: ["!", ["has", "point_count"]]
    });
  };
  
  const updateMarkers = (updatedHelicopterData) => {
    // Remove all existing markers
    for (const markKey in markers) {
        markers[markKey].remove();
        delete markers[markKey];
    }

    const features = map.current.querySourceFeatures('halicopters');
    const tempMarker = [];
    const featureId = [];
    for (const feature of features) {
        if (featureId.indexOf(feature.id) > -1) {
            continue;
        }
        if (feature.properties.cluster) {
            featureId.push(feature.id);
            continue;
        }
        featureId.push(feature.id);
        const coordinates = JSON.parse(feature.properties.coordinates);
        if (!markers[feature.id]) {
            tempMarker.push(feature.id);
            const el = document.createElement('div');
            el.id = feature.id;
            el.setAttribute('data-value', JSON.stringify(feature.properties));
            el.className = "helicopter-marker status" + feature.properties['Einsatzbereit_in'];
            el.innerHTML = '<p class="helicopter-icon-red-hover-text">' + feature.properties['title'] + '</p>' +
                '<p class="helicopter-icon-red-hover-text helicopter-status-text" id="hali_status_' + feature.id + '">' +
                (feature.properties.Einsatzbereit_in == "1" ? "15 Min" :
                    feature.properties.Einsatzbereit_in == "2" ? "30 Min" :
                        feature.properties.Einsatzbereit_in == "3" ? "60 Min" :
                            feature.properties.Einsatzbereit_in == "4" ? "2 Std" :
                                feature.properties.Einsatzbereit_in == "5" ? "3 Std" :
                                    feature.properties.Einsatzbereit_in == "6" ? "Im Einsatz" :
                                        feature.properties.Einsatzbereit_in == "7" ? "Abgemeldet N/A" :
                                            feature.properties.Einsatzbereit_in == "8" ? "Im Betrieb" : "") +
                '</p><img alt="heli1" src="' + feature.properties.icon + '" class="helicopter-icon-green-image"/>';
            markers[feature.id] = new mapboxgl.Marker({ element: el }).setLngLat(coordinates).addTo(map.current);
            el.addEventListener('click', (e) => toggleSidebar(e));
        } else {
            tempMarker.push(feature.id);
            if (currentFlyLoc[feature.id] && currentFlyLoc[feature.id][0] && currentFlyLoc[feature.id][0] !== coordinates[0]) {
                markers[feature.id].setLngLat(feature.geometry.coordinates);
            }
        }
    }
};

  return { mapContainer, map, toggleSidebar, addHelicopterClusterSourceAndLayers, resetHelicopterLocations };
};

export default useMap;